<template>
  <div class="row">
    <div class="col-xl-12">
      <!-- begin: Import ข้อมูลตามเป้าหมาย(แก้ไข) Modal — edit mode only; mirrors StockCoverDaysCreate.vue -->
      <div
        v-if="canEdit"
        class="modal fade"
        id="importEditStockCoverDaysModal"
        tabindex="-1"
        role="dialog"
        aria-hidden="true"
      >
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Import ข้อมูลตามเป้าหมาย(แก้ไข)</h5>
              <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div class="modal-body">
              <form ref="importEditForm">
                <div class="form-group">
                  <label>ไฟล์ Excel (.xlsx) <span class="text-danger">*</span></label>
                  <div class="custom-file">
                    <input
                      type="file"
                      class="custom-file-input"
                      id="importEditFile"
                      accept=".xlsx"
                      @change="onImportEditFileChange"
                      ref="importEditFileInput"
                    />
                    <label class="custom-file-label" for="importEditFile">
                      {{ importEditFileName || 'เลือกไฟล์...' }}
                    </label>
                  </div>
                  <small class="form-text text-muted">รองรับไฟล์ .xlsx เท่านั้น (แก้ไขข้อมูลเดิม)</small>
                </div>
              </form>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-dismiss="modal">ยกเลิก</button>
              <button
                type="button"
                class="btn btn-primary"
                @click="submitImportEdit"
                :disabled="!importEditFile"
              >
                <i class="fas fa-upload"></i> นำเข้าข้อมูล
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- end: Import ข้อมูลตามเป้าหมาย(แก้ไข) Modal -->

      <div class="kt-portlet kt-portlet--mobile">
        <!-- Header -->
        <div class="kt-portlet__head kt-portlet__head--lg">
          <div class="kt-portlet__head-label">
            <span class="kt-portlet__head-icon">
              <i :class="isViewMode ? 'fas fa-search' : 'fas fa-edit'"></i>
            </span>
            <h3 class="kt-portlet__head-title">
              {{ isViewMode ? 'ดูรายละเอียด Stock Cover Days' : 'แก้ไข Stock Cover Days' }}
              <!-- IssueList #26 / #71: edit-rule note shown via a blue info (i) icon. A native title= tooltip
                   can't render <br /> line breaks, so use a CSS hover tooltip with v-html for the two-line text. -->
              <span v-if="canEdit && detailData" class="scd-hint">
                <i class="fas fa-info-circle text-primary kt-margin-l-5"
                   style="cursor: help; font-size: 1rem;"></i>
                <span class="scd-hint-text" v-html="editHintHtml"></span>
              </span>
            </h3>
          </div>
          <div class="kt-portlet__head-toolbar">
            <button type="button" class="btn btn-secondary" @click="goBack">
              <i class="la la-close"></i> ย้อนกลับ
            </button>
          </div>
        </div>

        <div class="kt-portlet__body">
          <!-- Loading -->
          <div v-if="isLoading" class="text-center py-5">
            <div class="kt-spinner kt-spinner--lg kt-spinner--brand"></div>
            <div class="mt-3">กำลังโหลดข้อมูล...</div>
          </div>

          <!-- Error -->
          <div v-else-if="errorMessage" class="alert alert-danger" role="alert">
            <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
          </div>

          <!-- Content -->
          <div v-else-if="detailData">
            <!-- (edit-rule note relocated to the blue (i) tooltip next to the page title — IssueList #26) -->

            <!-- Info header -->
            <div class="row align-items-end mb-2">
              <div class="col-md-4">
                <label class="font-weight-bold">คลังสินค้า :</label>
                <div>[{{ detailData.plantCode }}] {{ detailData.plantName }}</div>
              </div>
              <div class="col-md-2">
                <label class="font-weight-bold">ปี :</label>
                <div>{{ detailData.year }}</div>
              </div>
              <div class="col-md-6 text-right">
                <!-- R2-25: หน้า View เลือกหน่วยได้ใน filter row ด้านล่าง · หน้า Edit คงเป็นลิตร (ช่องกรอกเป็นลิตร) -->
                <div v-if="!isViewMode" class="text-muted">หน่วย : ลิตร</div>
              </div>
            </div>

            <!-- Filters: ชื่อสินค้า (product) + Version (spec §4.2.5) — ค้นหา/ล้างค่าทั้งหมด moved to action row below (IssueList #19) -->
            <div class="row align-items-end mb-3">
              <div class="col-md-4">
                <label class="font-weight-bold">ชื่อสินค้า :</label>
                <select class="form-control form-control-sm" v-model="selectedProduct">
                  <option value="">ทั้งหมด</option>
                  <option v-for="p in productFilterOptions" :key="p.code" :value="p.code">
                    {{ p.name ? p.name + ' [' + p.code + ']' : p.code }}
                  </option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="font-weight-bold">Version ข้อมูล :</label>
                <select class="form-control form-control-sm" v-model="selectedVersion">
                  <option value="">ทั้งหมด</option>
                  <option v-for="v in versionOptions" :key="v" :value="String(v)">Version{{ v }}</option>
                </select>
              </div>
              <!-- R2-25: หน่วย (เฉพาะหน้า View) — ถัง = ลิตร ÷ NETVOLUME ของสินค้า ปัดครึ่งขึ้น (เหมือน F2/F4) -->
              <div v-if="isViewMode" class="col-md-2">
                <label class="font-weight-bold">หน่วย :</label>
                <select class="form-control form-control-sm" v-model="selectedUnit">
                  <option value="ลิตร">ลิตร</option>
                  <option value="ถัง">ถัง</option>
                </select>
              </div>
            </div>

            <!-- Actions: ค้นหา/ล้างค่าทั้งหมด (left) · Export Excel/Import(แก้ไข)/ดาวโหลด(แก้ไข) (right) — IssueList #19 -->
            <div class="row align-items-center mb-3">
              <div class="col-md-4">
                <button type="button" class="btn btn-primary" @click="applyFilters">
                  ค้นหา
                </button>
                <button type="button" class="btn btn-secondary" @click="clearFilters">
                  ล้างค่าทั้งหมด
                </button>
              </div>
              <div class="col-md-8 text-right">
                <button type="button" class="btn btn-success btn-sm" @click="exportExcel" :disabled="isExporting">
                  <i :class="isExporting ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'"></i> Export Excel
                </button>
                <button v-if="canEdit" type="button" class="btn btn-warning btn-sm" @click="showImportEditModal">
                  <i class="fas fa-file-import"></i> Import ข้อมูลตามเป้าหมาย(แก้ไข)
                </button>
                <button v-if="canEdit" type="button" class="btn btn-info btn-sm" @click="downloadExport" :disabled="isExporting">
                  <i :class="isExporting ? 'fas fa-spinner fa-spin' : 'fas fa-download'"></i> ดาวโหลด(แก้ไข)
                </button>
              </div>
            </div>

            <hr />

            <!-- Add button (edit only) — moved below hr, separated from header section (IssueList #19) -->
            <div v-if="canEdit" class="row mb-3">
              <div class="col-12 text-right">
                <button type="button" class="btn btn-primary btn-sm" @click="addNewCard">
                  <i class="fas fa-plus"></i> เพิ่มข้อมูลตามเป้าหมาย
                </button>
              </div>
            </div>

            <!-- New (blank) card — inserted at top -->
            <div v-if="newCard" class="card mb-4 border-primary">
              <div class="card-header bg-light d-flex justify-content-between align-items-center">
                <div class="d-flex align-items-center">
                  <label class="font-weight-bold mb-0 mr-2">ชื่อสินค้า :</label>
                  <div style="min-width: 260px;">
                    <select2
                      :css-style="'width: 100%;'"
                      :css-class="'form-control form-control-sm'"
                      :id="'scdNewProductSelect'"
                      :name="'scdNewProductSelect'"
                      :select2-options="productOptions"
                      v-model="newCard.productCode"
                      @selectChange="onNewProductChange"
                    ></select2>
                  </div>
                  <span class="ml-3 font-weight-bold">Version ข้อมูล : <span class="badge badge-info">Version ถัดไป (auto)</span></span>
                </div>
                <button type="button" class="btn btn-sm btn-danger" @click="removeNewCard" title="ยกเลิกการเพิ่ม">
                  <i class="la la-trash"></i>
                </button>
              </div>
              <div class="card-body p-2">
                <div class="table-responsive">
                  <table class="table table-bordered table-sm scd-matrix mb-0">
                    <tbody>
                      <!-- SaleDays -->
                      <tr>
                        <th class="scd-label scd-row-saledays">จำนวนวันขาย</th>
                        <td v-for="cell in newCard.cells" :key="'ns-' + cell.month" class="scd-row-saledays">
                          <input type="text" class="form-control form-control-sm text-center scd-input"
                            v-model="cell.saleDays" @keydown="onIntKeydown" @blur="onSaleDaysBlur(cell)" />
                        </td>
                      </tr>
                      <!-- Month header -->
                      <tr>
                        <th class="scd-label scd-row-months">ข้อมูลตามเป้าหมาย</th>
                        <td v-for="cell in newCard.cells" :key="'nm-' + cell.month" class="scd-row-months text-center font-weight-bold">
                          {{ monthLabels[cell.month - 1] }}
                        </td>
                      </tr>
                      <!-- Measures -->
                      <tr v-for="row in measureRows" :key="'nr-' + row.key">
                        <th class="scd-label" :class="row.cls">{{ row.label }}</th>
                        <td v-for="cell in newCard.cells" :key="'nrc-' + row.key + '-' + cell.month" :class="row.cls">
                          <input type="text" class="form-control form-control-sm text-center scd-input"
                            v-model="cell[row.key + 'Text']" @keydown="onIntKeydown"
                            @focus="onMeasureFocus(cell, row.key)" @blur="onMeasureBlur(cell, row.key)" />
                        </td>
                      </tr>
                      <!-- SCVD -->
                      <tr>
                        <th class="scd-label scd-row-scvd">SCVD</th>
                        <td v-for="cell in newCard.cells" :key="'nv-' + cell.month" class="scd-row-scvd text-center">
                          {{ formatScvd(displayScvd(newCard, cell)) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="text-right mt-2">
                  <button type="button" class="btn btn-primary btn-sm" @click="saveNewCard" :disabled="isSaving">
                    <i class="la la-save"></i> บันทึก
                  </button>
                </div>
              </div>
            </div>

            <!-- Existing cards -->
            <div v-if="visibleCards.length === 0 && !newCard" class="text-center text-muted py-4">
              ไม่มีข้อมูลตามเป้าหมาย
            </div>

            <div v-for="(card, ci) in visibleCards" :key="card.productCode + '-' + card.version" class="card mb-4">
              <div class="card-header bg-light d-flex justify-content-between align-items-center">
                <div>
                  <span class="font-weight-bold">ชื่อสินค้า : {{ card.productName }} [{{ card.productCode }}]</span>
                  <span class="ml-3 font-weight-bold">Version ข้อมูล : Version{{ card.version }}</span>
                </div>
                <button v-if="canEdit" type="button" class="btn btn-sm btn-danger" @click="deleteCard(card)" title="ลบ Version นี้">
                  <i class="la la-trash"></i>
                </button>
              </div>
              <div class="card-body p-2">
                <div class="table-responsive">
                  <table class="table table-bordered table-sm scd-matrix mb-0">
                    <tbody>
                      <!-- SaleDays -->
                      <tr>
                        <th class="scd-label scd-row-saledays">จำนวนวันขาย</th>
                        <td v-for="cell in card.cells" :key="'s-' + ci + '-' + cell.month" class="scd-row-saledays text-center">
                          <input v-if="cellEditable(card, cell)" type="text"
                            class="form-control form-control-sm text-center scd-input"
                            v-model="cell.saleDays" @keydown="onIntKeydown" @blur="onSaleDaysBlur(cell)" />
                          <span v-else>{{ cell.exists ? cell.saleDays : '' }}</span>
                        </td>
                      </tr>
                      <!-- Month header -->
                      <tr>
                        <th class="scd-label scd-row-months">ข้อมูลตามเป้าหมาย</th>
                        <td v-for="cell in card.cells" :key="'m-' + ci + '-' + cell.month" class="scd-row-months text-center font-weight-bold">
                          {{ monthLabels[cell.month - 1] }}
                        </td>
                      </tr>
                      <!-- Measures -->
                      <tr v-for="row in measureRows" :key="'r-' + ci + '-' + row.key">
                        <th class="scd-label" :class="row.cls">{{ row.label }}</th>
                        <td v-for="cell in card.cells" :key="'rc-' + ci + '-' + row.key + '-' + cell.month" :class="row.cls" class="text-center">
                          <input v-if="cellEditable(card, cell)" type="text"
                            class="form-control form-control-sm text-center scd-input"
                            v-model="cell[row.key + 'Text']" @keydown="onIntKeydown"
                            @focus="onMeasureFocus(cell, row.key)" @blur="onMeasureBlur(cell, row.key)" />
                          <span v-else>{{ cell.exists ? formatInt(displayMeasure(card, cell[row.key])) : '' }}</span>
                        </td>
                      </tr>
                      <!-- SCVD -->
                      <tr>
                        <th class="scd-label scd-row-scvd">SCVD</th>
                        <td v-for="cell in card.cells" :key="'v-' + ci + '-' + cell.month" class="scd-row-scvd text-center">
                          {{ formatScvd(displayScvd(card, cell)) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Save (in-place update) -->
            <div v-if="canEdit && visibleCards.length > 0" class="row">
              <div class="col-12 text-right">
                <hr />
                <button type="button" class="btn btn-primary" @click="saveUpdate" :disabled="isSaving">
                  <i class="la la-save"></i> {{ isSaving ? 'กำลังบันทึก...' : 'บันทึก' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import select2 from "../../Shared/Select2";

require("sweetalert2");

export default {
  components: {
    select2,
  },
  data: function () {
    var self = this;
    return {
      isLoading: false,
      isSaving: false,
      isExporting: false,
      errorMessage: "",
      detailData: null,
      cards: [],
      newCard: null,
      selectedVersion: "",
      selectedProduct: "",
      // Applied (Search-committed) filters — drive visibleCards; selected* are the pending dropdown values
      appliedVersion: "",
      appliedProduct: "",
      // R2-25: หน่วยแสดงผลหน้า View (ใช้เมื่อกดค้นหา เหมือน filter อื่น)
      selectedUnit: "ลิตร",
      appliedUnit: "ลิตร",
      // Import ข้อมูลตามเป้าหมาย(แก้ไข) state — mirrors StockCoverDaysCreate.vue import
      importEditFile: null,
      importEditFileName: "",
      lastImportEditRows: null,
      monthLabels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
      measureRows: [
        { key: "bgStockLitre", label: "BG Stock", cls: "scd-row-bg" },
        { key: "demandLitre", label: "Demand", cls: "scd-row-demand" },
        { key: "supplyLitre", label: "Supply", cls: "scd-row-supply" },
      ],
      // Product Select2 (สินค้าสำเร็จรูป น้ำเบียร์) — reuse existing endpoint (A3/F4)
      productOptions: {
        placeholder: "เลือกสินค้า",
        allowClear: true,
        ajax: {
          url: "/Api/Product/Select2Product",
          data: function (params) {
            return {
              search: params.term,
              page: params.page || 1,
              productGroupId: "14",
              productTypeId: "22",
              onlyStockCoverDays: true, // CR-StockCoverDays A3: เฉพาะ SKU ที่เปิดใช้งาน SCVD
            };
          },
          processResults: function (data) {
            return {
              results: data.results,
              pagination: { more: data.more },
            };
          },
          transport: function (params, success, failure) {
            params.beforeSend = function (request) {
              request.setRequestHeader(
                "Authorization",
                "Bearer " + self.$root.user.access_token
              );
            };
            var $request = $.ajax(params);
            $request.then(success);
            $request.fail(failure);
            return $request;
          },
        },
      },
    };
  },
  computed: {
    isViewMode: function () {
      return this.$route.meta.isViewMode === true;
    },
    // R2-30 — เปิดจากแถว "ปิดใช้งาน" ในหน้า List (/View/{plant}/{year}?status=inactive) · ใช้เฉพาะโหมดดู
    isInactiveView: function () {
      return this.isViewMode && this.$route.query.status === "inactive";
    },
    // Editable = not view route AND server says year >= current year (M12/§1.5)
    canEdit: function () {
      return !this.isViewMode && this.hasWriteRole && !!this.detailData && this.detailData.isEditable === true;
    },
    // QA BUG-13: สิทธิ์เขียน SCVD = Admin + Team (เงื่อนไขเดียวกับหน้า List) · Viewer ไม่ผ่าน → ใช้ได้แค่หน้า View
    hasWriteRole: function () {
      return this.$root.canShow([["SCVD_ADMIN", "COMPANY_PROFILE_TYPE_SCVD"], ["SCVD_TEAM_EDIT", "COMPANY_PROFILE_TYPE_SCVD"]]);
    },
    versionOptions: function () {
      var set = {};
      this.cards.forEach(function (c) {
        set[c.version] = true;
      });
      return Object.keys(set)
        .map(function (v) {
          return parseInt(v);
        })
        .sort(function (a, b) {
          return b - a;
        });
    },
    // Distinct products from the loaded cards (options for the ชื่อสินค้า filter)
    productFilterOptions: function () {
      var seen = {};
      var list = [];
      this.cards.forEach(function (c) {
        if (!seen[c.productCode]) {
          seen[c.productCode] = true;
          list.push({ code: c.productCode, name: c.productName || "" });
        }
      });
      return list;
    },
    // #71: two-line edit-rule hint shown in the (i) tooltip. Rendered via v-html so the <br /> is a real
    // line break; keeps the exact wording incl. leading "*" on each line and quotes around เพิ่มข้อมูลตามเป้าหมาย.
    editHintHtml: function () {
      return '*แก้ไขตัวเลขย้อนหลังที่หน้า Master ได้ภายในปีเดียวกันเท่านั้น เช่น ปี 2026 แก้ไขย้อนหลังได้ภายใน 1/1/2026 - 31/12/2026 เท่านั้น<br />*จะเพิ่ม Version ใหม่จากการ Import ต้องไปเพิ่มที่หน้า "เพิ่มข้อมูลตามเป้าหมาย" แต่ถ้าจะสร้างจาก Web สามารถสร้างจากหน้านี้ได้เลย';
    },
    // Cards shown = applied (Search-committed) product AND version filters
    visibleCards: function () {
      var p = this.appliedProduct;
      var v = this.appliedVersion;
      return this.cards.filter(function (c) {
        var okProduct = p === "" || p === null || String(c.productCode) === String(p);
        var okVersion = v === "" || v === null || String(c.version) === String(v);
        return okProduct && okVersion;
      }).sort(function (a, b) {
        // R2-12: group by product, show latest version first (version มาก→น้อย)
        if (String(a.productCode) !== String(b.productCode)) {
          return String(a.productCode) < String(b.productCode) ? -1 : 1;
        }
        return b.version - a.version;
      });
    },
  },
  // R2-26: เปลี่ยนตัวกรอง (ชื่อสินค้า / Version / หน่วย) แล้ว apply ทันที ไม่ต้องกดค้นหา
  //   (ปุ่มค้นหา/ล้างค่าทั้งหมดยังใช้ได้ · ตัวกรองมีผลแค่การแสดงผลฝั่งหน้าจอ ไม่ยิง API ใหม่)
  watch: {
    selectedProduct: function (v) { this.appliedProduct = v; },
    selectedVersion: function (v) { this.appliedVersion = v; },
    selectedUnit: function (v) { this.appliedUnit = v; },
  },
  mounted: function () {
    // QA BUG-13: Viewer เปิด /Edit ตรง ๆ → พาไปหน้า View ทันที (ไม่แสดงช่องแก้ไข/ปุ่มบันทึก)
    if (!this.isViewMode && !this.hasWriteRole) {
      this.$router.replace({ path: "/StockCoverDays/View/" + this.$route.params.plantCode + "/" + this.$route.params.year });
    }
    this.loadDetail();
  },
  methods: {
    goBack: function () {
      this.$router.push("/StockCoverDays/List");
    },

    loadDetail: async function () {
      var self = this;
      var plantCode = self.$route.params.plantCode;
      var year = self.$route.params.year;

      if (!plantCode || !year) {
        self.errorMessage = "ไม่พบข้อมูลคลังสินค้าหรือปี";
        return;
      }

      self.isLoading = true;
      self.errorMessage = "";
      try {
        var response = await axios.get("/Api/StockCoverDayTarget/Detail", {
          headers: { Authorization: "Bearer " + self.$root.user.access_token },
          params: { plantCode: plantCode, year: year, status: self.isInactiveView ? "inactive" : undefined },
        });

        if (response.data && response.data.success && response.data.data) {
          self.detailData = response.data.data;
          self.cards = (self.detailData.cards || []).map(function (card) {
            return {
              productCode: card.productCode,
              productName: card.productName || "",
              version: card.version,
              netVolume: card.netVolume || 0,   // R2-25: ลิตรต่อถัง สำหรับแปลงหน่วย
              isNew: false,
              cells: self.buildCells(card.months),
            };
          });
          self.newCard = null;
          // reset filters so a fresh load shows all cards (spec default = ทั้งหมด)
          self.selectedProduct = "";
          self.selectedVersion = "";
          self.appliedProduct = "";
          self.appliedVersion = "";
          self.selectedUnit = "ลิตร";
          self.appliedUnit = "ลิตร";
          // R2-28: เปิด /Edit ของปีที่แก้ไขไม่ได้ (ปีเก่า · isEditable=false) → พาไปหน้า View แทน
          //   (View/Edit ใช้ component เดียวกัน · โหมดอิง $route.meta → สลับเป็นโหมดดูทันทีโดยไม่โหลดใหม่)
          if (!self.isViewMode && self.detailData.isEditable === false) {
            self.$router.replace({ path: "/StockCoverDays/View/" + plantCode + "/" + year });
          }
        } else {
          self.errorMessage = (response.data && response.data.message) || "ไม่สามารถโหลดข้อมูลได้";
        }
      } catch (error) {
        console.error("Error loading detail:", error);
        self.errorMessage = "เกิดข้อผิดพลาดในการโหลดข้อมูล";
      } finally {
        self.isLoading = false;
      }
    },

    // Normalize a card's months into a fixed 12-slot array (M1: may be < 12 months)
    buildCells: function (months) {
      var map = {};
      (months || []).forEach(function (m) {
        map[m.month] = m;
      });
      var cells = [];
      for (var mo = 1; mo <= 12; mo++) {
        var s = map[mo];
        if (s) {
          cells.push({
            month: mo,
            exists: true,
            saleDays: s.saleDays,
            bgStockLitre: s.bgStockLitre,
            demandLitre: s.demandLitre,
            supplyLitre: s.supplyLitre,
            // formatted display twins the inputs bind to (numeric field stays source of truth)
            bgStockLitreText: this.formatInt(s.bgStockLitre),
            demandLitreText: this.formatInt(s.demandLitre),
            supplyLitreText: this.formatInt(s.supplyLitre),
            scvd: s.scvd,
            // IssueList #30: snapshot the loaded values → modified-only save (send/count only changed cells)
            _orig: { saleDays: s.saleDays, bgStockLitre: s.bgStockLitre, demandLitre: s.demandLitre, supplyLitre: s.supplyLitre },
          });
        } else {
          cells.push({
            month: mo,
            exists: false,
            saleDays: null,
            bgStockLitre: null,
            demandLitre: null,
            supplyLitre: null,
            bgStockLitreText: "",
            demandLitreText: "",
            supplyLitreText: "",
            scvd: null,
            _orig: { saleDays: null, bgStockLitre: null, demandLitre: null, supplyLitre: null },
          });
        }
      }
      return cells;
    },

    cellEditable: function (card, cell) {
      if (card.isNew) return true;
      // IssueList #30: allow editing months that were not imported (cell.exists === false) too,
      // so the user can fill a previously-empty month and save it into the same version.
      return this.canEdit;
    },

    // ---- SCVD live preview (F6 mirror: AwayFromZero, 2dp; NULL when saleDays==0 || demand==0) ----
    scvdCompute: function (bg, demand, saleDays) {
      var b = this.toIntOrNull(bg);
      var d = this.toIntOrNull(demand);
      var s = this.toIntOrNull(saleDays);
      if (b === null || d === null || s === null) return null;
      if (s === 0 || d === 0) return null;
      var raw = (b * s) / d; // values >= 0 → Math.round = round-half-up = AwayFromZero
      return Math.round(raw * 100) / 100;
    },
    displayScvd: function (card, cell) {
      if (this.cellEditable(card, cell)) {
        return this.scvdCompute(cell.bgStockLitre, cell.demandLitre, cell.saleDays);
      }
      return cell.scvd;
    },

    // ---- Numeric input guards (F5) ----
    onIntKeydown: function (event) {
      var allowed = ["Backspace", "Delete", "Tab", "Escape", "Enter", "Home", "End", "ArrowLeft", "ArrowRight"];
      if (allowed.indexOf(event.key) !== -1) return;
      if ((event.ctrlKey || event.metaKey) && ["a", "c", "v", "x"].indexOf(event.key.toLowerCase()) !== -1) return;
      if (/^[0-9]$/.test(event.key)) return;
      event.preventDefault();
    },
    onSaleDaysBlur: function (cell) {
      if (this.isBlank(cell.saleDays)) {
        cell.saleDays = null;
        return;
      }
      var v = parseInt(cell.saleDays);
      if (isNaN(v) || v < 0) v = 0;
      if (v > 31) {
        Swal.fire("แจ้งเตือน", "จำนวนวันขายต้องอยู่ระหว่าง 0-31 วัน", "warning");
        v = 31;
      }
      cell.saleDays = v;
    },
    // BG / Demand / Supply — thousands-format on blur, strip on focus (mirror StockCoverDaysCreate.vue).
    // Source of truth = numeric cell[key]; cell[key+'Text'] is the formatted display twin the input binds to.
    stripThousands: function (v) {
      return String(v === null || v === undefined ? "" : v).replace(/,/g, "");
    },
    formatThousands: function (v) {
      // reuse formatInt (expects a comma-free value → strip first)
      return this.formatInt(this.stripThousands(v));
    },
    // focus → strip formatting back to plain digits for editing
    onMeasureFocus: function (cell, key) {
      cell[key + "Text"] = this.stripThousands(cell[key + "Text"]);
    },
    // blur → parse, clamp >= 0, store numeric (source of truth) + formatted display twin
    onMeasureBlur: function (cell, key) {
      var raw = this.stripThousands(cell[key + "Text"]).trim();
      if (raw === "") {
        cell[key] = null;
        cell[key + "Text"] = "";
        return;
      }
      var v = parseInt(raw, 10);
      if (isNaN(v) || v < 0) v = 0;
      cell[key] = v; // numeric — SCVD (displayScvd) + save payload (toInt) read this
      cell[key + "Text"] = this.formatInt(v); // formatted display twin
    },

    // ---- Add new (blank) card at top ----
    addNewCard: function () {
      if (this.newCard) {
        Swal.fire("แจ้งเตือน", "กรุณาบันทึกข้อมูลที่เพิ่มก่อนหน้าให้เสร็จก่อน", "warning");
        return;
      }
      var cells = [];
      for (var m = 1; m <= 12; m++) {
        cells.push({
          month: m,
          exists: true,
          saleDays: null,
          bgStockLitre: null,
          demandLitre: null,
          supplyLitre: null,
          // formatted display twins (blank to start; numeric field stays source of truth)
          bgStockLitreText: "",
          demandLitreText: "",
          supplyLitreText: "",
          scvd: null,
        });
      }
      this.newCard = { productCode: null, productName: "", version: null, isNew: true, cells: cells };
    },
    removeNewCard: function () {
      var self = this;
      Swal.fire({
        title: "ยกเลิกการเพิ่ม",
        text: "คุณต้องการยกเลิกการเพิ่มข้อมูลนี้หรือไม่?",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "ใช่",
        cancelButtonText: "ไม่",
      }).then(function (result) {
        if (result.value) {
          self.newCard = null;
        }
      });
    },
    onNewProductChange: function (id, value, data) {
      if (!this.newCard) return;
      this.newCard.productCode = value;
      this.newCard.productName = data && data.length > 0 ? data[0].text : "";
    },

    // ---- Save new card → POST /Save (new version snapshot, M5 gate) ----
    saveNewCard: async function () {
      var self = this;
      if (!self.newCard.productCode) {
        Swal.fire("กรุณากรอกข้อมูล", "กรุณาเลือกสินค้า", "warning");
        return;
      }
      var months = self.newCard.cells
        .filter(function (c) {
          return self.hasCellData(c);
        })
        .map(function (c) {
          return {
            month: c.month,
            saleDays: self.toInt(c.saleDays),
            bgStockLitre: self.toInt(c.bgStockLitre),
            demandLitre: self.toInt(c.demandLitre),
            supplyLitre: self.toInt(c.supplyLitre),
          };
        });

      if (months.length === 0) {
        Swal.fire("กรุณากรอกข้อมูล", "กรุณากรอกข้อมูลอย่างน้อย 1 เดือน", "warning");
        return;
      }

      await self.postSave(months, false);
    },
    postSave: async function (months, confirmNewVersion) {
      var self = this;
      var payload = {
        plantCode: self.detailData.plantCode,
        year: parseInt(self.detailData.year),
        confirmNewVersion: confirmNewVersion,
        products: [{ productCode: self.newCard.productCode, months: months }],
      };

      try {
        self.isSaving = true;
        KTApp.blockPage({ opacity: 0.05, message: "กำลังบันทึกข้อมูล..." });
        var response = await axios.post("/Api/StockCoverDayTarget/Save", payload, {
          headers: { Authorization: "Bearer " + self.$root.user.access_token },
        });
        KTApp.unblockPage();
        self.isSaving = false;

        var d = response.data;
        if (d && d.needVersionConfirm) {
          // P5: saving for the CURRENT year re-calculates snapshots of already-closed months of this year.
          var reCalcWarn = parseInt(self.detailData.year, 10) === new Date().getFullYear()
            ? "<br><br><b style='color:#b45309'>⚠️ ปีปัจจุบัน:</b> การบันทึกจะ<b>คำนวณ snapshot ของเดือนที่ปิดรอบแล้ว</b>ในปีนี้ใหม่ (re-calculate) โดยอัตโนมัติ"
            : "";
          var confirm = await Swal.fire({
            title: "พบข้อมูลเดิม",
            html: (d.message || "พบข้อมูลของสินค้านี้อยู่แล้ว ต้องการเพิ่มเป็น Version ใหม่ใช่หรือไม่?") + reCalcWarn,
            type: "question",
            showCancelButton: true,
            confirmButtonText: "ยืนยัน",
            cancelButtonText: "ย้อนกลับแก้ไข",
          });
          if (confirm.value) {
            await self.postSave(months, true);
          }
          return;
        }

        if (d && d.success) {
          await Swal.fire("บันทึกสำเร็จ", d.message || "บันทึกข้อมูลเรียบร้อยแล้ว", "success");
          self.newCard = null;
          self.selectedVersion = "";
          await self.loadDetail();
        } else {
          Swal.fire("เกิดข้อผิดพลาด", (d && d.message) || "ไม่สามารถบันทึกข้อมูลได้", "error");
        }
      } catch (error) {
        KTApp.unblockPage();
        self.isSaving = false;
        console.error("Error saving:", error);
        var message = "ไม่สามารถบันทึกข้อมูลได้";
        if (error.response && error.response.data && error.response.data.message) {
          message = error.response.data.message;
        }
        if (error.response && error.response.status === 403) {
          message = "คุณไม่มีสิทธิ์บันทึกข้อมูล กรุณาติดต่อผู้ดูแลระบบ"; // QA BUG-05
        }
        Swal.fire("เกิดข้อผิดพลาด", message, "error");
      }
    },

    // ---- Save in-place update → PUT /Update (B.6, no new version) ----
    saveUpdate: async function () {
      var self = this;
      // P5: saving edits for the CURRENT year re-calculates snapshots of already-closed months of this year.
      var reCalcWarn = parseInt(self.detailData.year, 10) === new Date().getFullYear()
        ? "<br><br><b style='color:#b45309'>⚠️ ปีปัจจุบัน:</b> การบันทึกจะ<b>คำนวณ snapshot ของเดือนที่ปิดรอบแล้ว</b>ในปีนี้ใหม่ (re-calculate) โดยอัตโนมัติ"
        : "";
      var confirmResult = await Swal.fire({
        title: "ยืนยันการบันทึก",
        html: "คุณต้องการบันทึกการแก้ไขข้อมูลนี้หรือไม่?" + reCalcWarn,
        type: "question",
        showCancelButton: true,
        confirmButtonText: "ยืนยัน",
        cancelButtonText: "ยกเลิก",
      });
      if (!confirmResult.value) return;

      // IssueList #30: MODIFIED-ONLY save. A cell is "modified" if it is a newly-filled empty month OR an
      // existing month whose value changed from what was loaded (_orig). Only modified cells are sent + counted.
      var hasAnyValue = function (c) {
        return !self.isBlank(c.saleDays) || !self.isBlank(c.bgStockLitre)
          || !self.isBlank(c.demandLitre) || !self.isBlank(c.supplyLitre);
      };
      var isComplete = function (c) {
        return !self.isBlank(c.saleDays) && !self.isBlank(c.bgStockLitre)
          && !self.isBlank(c.demandLitre) && !self.isBlank(c.supplyLitre);
      };
      var isModified = function (c) {
        if (!c.exists) return hasAnyValue(c); // new month → modified once the user fills anything
        var o = c._orig || {};
        return self.toInt(c.saleDays) !== self.toInt(o.saleDays)
          || self.toInt(c.bgStockLitre) !== self.toInt(o.bgStockLitre)
          || self.toInt(c.demandLitre) !== self.toInt(o.demandLitre)
          || self.toInt(c.supplyLitre) !== self.toInt(o.supplyLitre);
      };
      // every MODIFIED cell must have ALL fields filled (บังคับกรอกครบ) — partial new/edited month is rejected.
      var badCell = null;
      self.cards.forEach(function (card) {
        card.cells.forEach(function (c) {
          if (!badCell && isModified(c) && !isComplete(c)) {
            badCell = { p: card.productName || card.productCode, m: self.monthLabels[c.month - 1] };
          }
        });
      });
      if (badCell) {
        Swal.fire(
          "ข้อมูลไม่ครบ",
          "กรุณากรอกให้ครบทุกช่อง (จำนวนวันขาย / BG Stock / Demand / Supply) — สินค้า " + badCell.p + " เดือน " + badCell.m,
          "warning"
        );
        return;
      }

      var payloadCards = self.cards
        .map(function (card) {
          return {
            productCode: card.productCode,
            version: card.version,
            months: card.cells
              .filter(isModified) // #30: only changed existing months + newly-filled months
              .map(function (c) {
                return {
                  month: c.month,
                  saleDays: self.toInt(c.saleDays),
                  bgStockLitre: self.toInt(c.bgStockLitre),
                  demandLitre: self.toInt(c.demandLitre),
                  supplyLitre: self.toInt(c.supplyLitre),
                };
              }),
          };
        })
        .filter(function (card) {
          return card.months.length > 0; // drop products with no changes
        });

      // nothing changed → skip the API call
      var changedCount = payloadCards.reduce(function (n, c) { return n + c.months.length; }, 0);
      if (changedCount === 0) {
        Swal.fire("ไม่มีการเปลี่ยนแปลง", "ไม่พบข้อมูลที่แก้ไข", "info");
        return;
      }

      var payload = {
        plantCode: self.detailData.plantCode,
        year: parseInt(self.detailData.year),
        cards: payloadCards,
      };

      try {
        self.isSaving = true;
        KTApp.blockPage({ opacity: 0.05, message: "กำลังบันทึกข้อมูล..." });
        var response = await axios.put("/Api/StockCoverDayTarget/Update", payload, {
          headers: { Authorization: "Bearer " + self.$root.user.access_token },
        });
        KTApp.unblockPage();
        self.isSaving = false;

        if (response.data && response.data.success) {
          await Swal.fire("บันทึกสำเร็จ", response.data.message || "บันทึกข้อมูลเรียบร้อยแล้ว", "success");
          await self.loadDetail();
        } else {
          Swal.fire("เกิดข้อผิดพลาด", (response.data && response.data.message) || "ไม่สามารถบันทึกข้อมูลได้", "error");
        }
      } catch (error) {
        KTApp.unblockPage();
        self.isSaving = false;
        console.error("Error updating:", error);
        var message = "ไม่สามารถบันทึกข้อมูลได้";
        if (error.response && error.response.data && error.response.data.message) {
          message = error.response.data.message;
        }
        if (error.response && error.response.status === 403) {
          message = "คุณไม่มีสิทธิ์บันทึกข้อมูล กรุณาติดต่อผู้ดูแลระบบ"; // QA BUG-05
        }
        Swal.fire("เกิดข้อผิดพลาด", message, "error");
      }
    },

    // ---- Delete a card (soft) → POST /Delete (grain product×version, A7) ----
    deleteCard: function (card) {
      var self = this;
      Swal.fire({
        title: "ยืนยันการลบ",
        text: "คุณต้องการลบข้อมูล " + card.productName + " Version" + card.version + " หรือไม่?",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "ใช่, ลบ",
        cancelButtonText: "ยกเลิก",
      }).then(async function (result) {
        if (!result.value) return;
        try {
          KTApp.blockPage({ opacity: 0.05, message: "กำลังลบข้อมูล..." });
          var response = await axios.post(
            "/Api/StockCoverDayTarget/Delete",
            {
              plantCode: self.detailData.plantCode,
              productCode: card.productCode,
              year: parseInt(self.detailData.year),
              version: card.version,
            },
            { headers: { Authorization: "Bearer " + self.$root.user.access_token } }
          );
          KTApp.unblockPage();

          if (response.data && response.data.success) {
            await Swal.fire("ลบสำเร็จ", response.data.message || "ลบข้อมูลเรียบร้อยแล้ว", "success");
            await self.loadDetail();
          } else {
            Swal.fire("เกิดข้อผิดพลาด", (response.data && response.data.message) || "ไม่สามารถลบข้อมูลได้", "error");
          }
        } catch (error) {
          KTApp.unblockPage();
          console.error("Error deleting:", error);
          Swal.fire("เกิดข้อผิดพลาด", "ไม่สามารถลบข้อมูลได้", "error");
        }
      });
    },

    // ---- Download(แก้ไข) → GET /Export (blob) F11 ----
    downloadExport: async function () {
      var self = this;
      try {
        self.isExporting = true;
        var params = new URLSearchParams();
        params.append("plantCode", self.detailData.plantCode);
        params.append("year", self.detailData.year);
        if (self.appliedVersion !== "") {
          params.append("version", self.appliedVersion);
        }
        if (self.appliedProduct !== "") {
          params.append("productCode", self.appliedProduct); // IssueList #27: match the on-screen product filter
        }
        params.append("forEdit", "true"); // IssueList #26: grey the read-only columns in the edit download

        var response = await axios.get("/Api/StockCoverDayTarget/Export?" + params.toString(), {
          headers: { Authorization: "Bearer " + self.$root.user.access_token },
          responseType: "blob",
        });

        self.isExporting = false;
        self.downloadBlobResponse(
          response,
          "StockCoverDays_" + self.detailData.plantCode + "_" + self.detailData.year + "_edit.xlsx"
        );
      } catch (error) {
        self.isExporting = false;
        console.error("Error exporting:", error);
        var message = "ไม่สามารถดาวน์โหลดข้อมูลได้";
        if (error.response && error.response.status === 404) {
          message = "ไม่พบข้อมูลสำหรับดาวน์โหลด";
        }
        Swal.fire("เกิดข้อผิดพลาด", message, "error");
      }
    },

    // ---- Plain Export Excel (currently-shown data) → GET /Export (F11) ----
    exportExcel: async function () {
      var self = this;
      try {
        self.isExporting = true;
        var params = new URLSearchParams();
        params.append("plantCode", self.detailData.plantCode);
        params.append("year", self.detailData.year);
        if (self.appliedVersion !== "") {
          params.append("version", self.appliedVersion);
        }
        // ASSUMPTION: F11 /Export accepts plantCode/year/version only (no productCode param in controller).
        // The product filter is applied client-side to the view; send productCode too so a future BE can honour it.
        if (self.appliedProduct !== "") {
          params.append("productCode", self.appliedProduct);
        }
        // R2-25: Export ตามหน่วยที่เลือก (หน้า View) · หน้า Edit = ลิตร
        params.append("unit", self.isViewMode ? self.appliedUnit : "ลิตร");
        // R2-30: View ของแถวปิดใช้งาน → export เฉพาะรายการที่ปิดใช้งาน
        if (self.isInactiveView) {
          params.append("status", "inactive");
        }
        var response = await axios.get("/Api/StockCoverDayTarget/Export?" + params.toString(), {
          headers: { Authorization: "Bearer " + self.$root.user.access_token },
          responseType: "blob",
        });
        self.isExporting = false;
        self.downloadBlobResponse(
          response,
          "StockCoverDays_" + self.detailData.plantCode + "_" + self.detailData.year + ".xlsx"
        );
      } catch (error) {
        self.isExporting = false;
        console.error("Error exporting:", error);
        var message = "ไม่สามารถดาวน์โหลดข้อมูลได้";
        if (error.response && error.response.status === 404) {
          message = "ไม่พบข้อมูลสำหรับดาวน์โหลด";
        }
        Swal.fire("เกิดข้อผิดพลาด", message, "error");
      }
    },

    // ---- Filters (ชื่อสินค้า + Version) — ค้นหา / ล้างค่าทั้งหมด (spec §4.2.5) ----
    applyFilters: function () {
      this.appliedProduct = this.selectedProduct;
      this.appliedVersion = this.selectedVersion;
      this.appliedUnit = this.selectedUnit;
    },
    clearFilters: function () {
      this.selectedProduct = "";
      this.selectedVersion = "";
      this.appliedProduct = "";
      this.appliedVersion = "";
      this.selectedUnit = "ลิตร";
      this.appliedUnit = "ลิตร";
    },
    // R2-25: ค่า BG/Demand/Supply ในหน่วยที่เลือก (เฉพาะหน้า View) — ค่าเก็บเป็นลิตร ·
    //   ถัง = ลิตร ÷ NETVOLUME ของสินค้า ปัดครึ่งขึ้นเป็นจำนวนเต็ม (ตรงกับ ScvdUnitConverter ฝั่ง BE)
    displayMeasure: function (card, litres) {
      if (!this.isViewMode || this.appliedUnit !== "ถัง") return litres;
      if (this.isBlank(litres)) return litres;
      var nv = Number(card && card.netVolume);
      if (!(nv > 0)) return litres;
      return Math.round(Number(litres) / nv);
    },

    // ---- Shared blob → file download (used by Export/ดาวโหลด/Error Detail) ----
    downloadBlobResponse: function (response, fallbackName) {
      var filename = fallbackName;
      var contentDisposition = response.headers["content-disposition"];
      if (contentDisposition) {
        var match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          filename = decodeURIComponent(match[1].replace(/['"]/g, ""));
        }
      }
      var blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      var link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
    },

    // ---- Import ข้อมูลตามเป้าหมาย(แก้ไข) — validate → preview → confirm (edit-mode only) ----
    // Mirrors StockCoverDaysCreate.vue import flow. Endpoints ImportEdit/ImportEditConfirm are
    // being built by BE (wire FE only). Error Detail reuses F9 /ExportErrors.
    showImportEditModal: function () {
      this.resetImportEditForm();
      $("#importEditStockCoverDaysModal").modal("show");
    },
    resetImportEditForm: function () {
      this.importEditFile = null;
      this.importEditFileName = "";
      if (this.$refs.importEditFileInput) {
        this.$refs.importEditFileInput.value = "";
      }
    },
    onImportEditFileChange: function (e) {
      var file = e.target.files[0];
      if (file) {
        var ext = file.name.split(".").pop().toLowerCase();
        if (ext !== "xlsx") {
          Swal.fire("ผิดพลาด", "กรุณาเลือกไฟล์ .xlsx เท่านั้น", "error");
          this.$refs.importEditFileInput.value = "";
          this.importEditFile = null;
          this.importEditFileName = "";
          return;
        }
        this.importEditFile = file;
        this.importEditFileName = file.name;
      } else {
        this.importEditFile = null;
        this.importEditFileName = "";
      }
    },
    // Step 1 — validate + preview (NO write) → POST /ImportEdit
    submitImportEdit: function () {
      if (!this.importEditFile) {
        Swal.fire("ผิดพลาด", "กรุณาเลือกไฟล์ Excel", "warning");
        return;
      }
      var self = this;
      var formData = new FormData();
      formData.append("file", this.importEditFile);
      // ASSUMPTION: BE ImportEdit uses the loaded plant/year as the edit context — send them along.
      formData.append("plantCode", this.detailData.plantCode);
      formData.append("year", this.detailData.year);
      KTApp.blockPage({ opacity: 0.05, message: "กำลังตรวจสอบข้อมูล กรุณารอสักครู่..." });
      axios
        .post("/Api/StockCoverDayTarget/ImportEdit", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: "Bearer " + self.$root.user.access_token,
          },
        })
        .then(function (response) {
          if (response.data.success) {
            $("#importEditStockCoverDaysModal").modal("hide");
            var preview = response.data.preview || {};
            var rows = response.data.importRows || [];
            // ASSUMPTION: preview shape mirrors /Import; edit-count key may be updateCount (fallback existingGroupCount).
            var editCount = preview.updateCount != null ? preview.updateCount : (preview.existingGroupCount || 0);
            var html = "<p>ตรวจสอบข้อมูลสำเร็จ พร้อมนำเข้า (แก้ไข)</p>";
            html += "<p>- จำนวนแถวทั้งหมด: " + (preview.totalRows || 0) + " รายการ<br/>";
            html += "- รายการที่จะแก้ไข: " + editCount + " กลุ่ม</p>";
            // P5: whole page is one plant+year; importing edits for the CURRENT year re-calculates
            // snapshots of already-closed months of this year.
            if (parseInt(self.detailData.year, 10) === new Date().getFullYear()) {
              html += "<br><br><b style='color:#b45309'>⚠️ ปีปัจจุบัน:</b> การบันทึกจะ<b>คำนวณ snapshot ของเดือนที่ปิดรอบแล้ว</b>ในปีนี้ใหม่ (re-calculate) โดยอัตโนมัติ";
            }
            Swal.fire({
              title: "ยืนยันการนำเข้าข้อมูล(แก้ไข)",
              html: html,
              type: "info",
              showCancelButton: true,
              confirmButtonText: "ยืนยันนำเข้า",
              cancelButtonText: "ยกเลิก",
              showLoaderOnConfirm: true,
            }).then(function (result) {
              if (result.value) {
                self.confirmImportEdit(rows);
              }
            });
          } else {
            self.lastImportEditRows = response.data.importRows || null;
            var errorHtml = "<p>" + (response.data.message || "นำเข้าข้อมูลไม่สำเร็จ") + "</p>";
            if (self.lastImportEditRows && self.lastImportEditRows.length > 0) {
              errorHtml += "<p>คลิก <strong>ดาวน์โหลด Error Detail</strong> เพื่อดูรายละเอียด</p>";
            }
            Swal.fire({
              title: "นำเข้าข้อมูลไม่สำเร็จ",
              html: errorHtml,
              type: "error",
              showCancelButton: self.lastImportEditRows && self.lastImportEditRows.length > 0,
              confirmButtonText: "ตกลง",
              cancelButtonText: '<i class="fas fa-download"></i> ดาวน์โหลด Error Detail',
              cancelButtonColor: "#5867dd",
            }).then(function (result) {
              if (result.dismiss === Swal.DismissReason.cancel) {
                self.downloadImportEditErrorDetail();
              }
            });
          }
        })
        .catch(function (error) {
          var message = "เกิดข้อผิดพลาดในการนำเข้าข้อมูล";
          if (error.response && error.response.data && error.response.data.message) {
            message = error.response.data.message;
          }
          Swal.fire("เกิดข้อผิดพลาด", message, "error");
        })
        .then(function () {
          KTApp.unblockPage();
        });
    },
    // Step 2 — commit validated rows → POST /ImportEditConfirm → reload detail
    confirmImportEdit: function (rows) {
      var self = this;
      KTApp.blockPage({ opacity: 0.05, message: "กำลังนำเข้าข้อมูล กรุณารอสักครู่..." });
      axios
        .post(
          "/Api/StockCoverDayTarget/ImportEditConfirm",
          { rows: rows },
          { headers: { Authorization: "Bearer " + self.$root.user.access_token } }
        )
        .then(function (response) {
          if (response.data.success) {
            // ASSUMPTION: confirm returns updatedCount (fallback createdCount).
            var n = response.data.updatedCount != null ? response.data.updatedCount : (response.data.createdCount || 0);
            Swal.fire({
              title: "สำเร็จ",
              html: "นำเข้าข้อมูล(แก้ไข)สำเร็จ " + n + " รายการ",
              type: "success",
            }).then(function () {
              self.loadDetail();
            });
          } else {
            Swal.fire("เกิดข้อผิดพลาด", response.data.message, "error");
          }
        })
        .catch(function (error) {
          var message = "เกิดข้อผิดพลาดในการนำเข้าข้อมูล";
          if (error.response && error.response.data && error.response.data.message) {
            message = error.response.data.message;
          }
          Swal.fire("เกิดข้อผิดพลาด", message, "error");
        })
        .then(function () {
          KTApp.unblockPage();
        });
    },
    // Error Detail — F9 (edit) POST /ExportEditErrors → keeps Version + ID columns (IssueList #26)
    downloadImportEditErrorDetail: function () {
      var self = this;
      if (!self.lastImportEditRows || self.lastImportEditRows.length === 0) {
        Swal.fire("ผิดพลาด", "ไม่พบข้อมูลสำหรับ export", "warning");
        return;
      }
      KTApp.blockPage({ opacity: 0.05, message: "กำลังสร้างไฟล์ กรุณารอสักครู่..." });
      axios
        .post(
          "/Api/StockCoverDayTarget/ExportEditErrors",
          { importRows: self.lastImportEditRows },
          { responseType: "blob", headers: { Authorization: "Bearer " + self.$root.user.access_token } }
        )
        .then(function (response) {
          self.downloadBlobResponse(response, "StockCoverDays_ImportEditErrors.xlsx");
        })
        .catch(function () {
          Swal.fire("เกิดข้อผิดพลาด", "ไม่สามารถดาวน์โหลดไฟล์ได้", "error");
        })
        .then(function () {
          KTApp.unblockPage();
        });
    },

    // ---- Helpers ----
    isBlank: function (v) {
      return v === "" || v === null || v === undefined;
    },
    isNumeric: function (v) {
      return !this.isBlank(v) && !isNaN(v);
    },
    hasCellData: function (cell) {
      return (
        this.isNumeric(cell.saleDays) ||
        this.isNumeric(cell.bgStockLitre) ||
        this.isNumeric(cell.demandLitre) ||
        this.isNumeric(cell.supplyLitre)
      );
    },
    toInt: function (v) {
      if (this.isBlank(v)) return 0;
      var n = parseInt(v);
      return isNaN(n) ? 0 : n;
    },
    toIntOrNull: function (v) {
      if (this.isBlank(v)) return null;
      var n = parseInt(v);
      return isNaN(n) ? null : n;
    },
    formatInt: function (v) {
      if (this.isBlank(v)) return "";
      return Number(v).toLocaleString("en-US");
    },
    formatScvd: function (v) {
      if (v === null || v === undefined) return "-";
      return Number(v).toFixed(2);
    },
  },
};
</script>

<style scoped>
.card {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.card-header {
  padding: 0.5rem 1rem;
}
.scd-matrix {
  min-width: 900px;
}
.scd-matrix th,
.scd-matrix td {
  padding: 0.25rem 0.35rem;
  vertical-align: middle;
  white-space: nowrap;
}
.scd-label {
  width: 130px;
  text-align: left;
  font-weight: 600;
}
.scd-input {
  min-width: 56px;
  padding: 0.15rem 0.25rem;
}
/* §1.2.1 row colors (reference จาก Excel) */
.scd-row-saledays {
  background-color: #d9d9d9;
}
.scd-row-months {
  background-color: #ffe599;
}
.scd-row-bg {
  background-color: #cfe2f3;
}
.scd-row-demand {
  background-color: #d9ead3;
}
.scd-row-supply {
  background-color: #ead1dc;
}
.scd-row-scvd {
  background-color: #d9d2e9;
}
/* #71: CSS hover tooltip for the edit-rule (i) hint — supports the two-line <br /> text via v-html */
.scd-hint {
  position: relative;
  display: inline-block;
}
.scd-hint-text {
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  min-width: 320px;
  max-width: 560px;
  margin-top: 6px;
  padding: 8px 12px;
  background-color: #333;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 400;
  line-height: 1.5;
  white-space: normal;
  text-align: left;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}
.scd-hint:hover .scd-hint-text,
.scd-hint:focus-within .scd-hint-text {
  display: block;
}
</style>



// WEBPACK FOOTER //
// App/Components/StockCoverDays/StockCoverDaysDetail.vue